import type {
  ClientErrorResponse,
  CoverageResponse,
  CreateCoverageRequest,
  UpdateCoverageRequest,
} from '@store/generated/patient-coverage';

// Re-export error type for easy access
export type { ClientErrorResponse };

/**
 * Patient coverage - represents insurance or payment information
 * Re-exported from generated API with feature-friendly name
 */
export type PatientCoverage = CoverageResponse;

/**
 * Request type for creating a new coverage
 */
export type CreatePatientCoverageRequest = CreateCoverageRequest;

/**
 * Request type for updating an existing coverage
 */
export type UpdatePatientCoverageRequest = UpdateCoverageRequest;

/**
 * Coverage kind enum for type safety
 */
export type CoverageKind = 'insurance' | 'self-pay' | 'other';

/**
 * Coverage order enum for type safety
 */
export type CoverageOrder = 1 | 2 | 3; // primary, secondary, tertiary

/**
 * Relationship to subscriber options
 */
export type RelationshipToSubscriber = 'self' | 'spouse' | 'child' | 'other';

/**
 * Helper type for organizing coverages by order
 */
export interface OrganizedCoverages {
  primary?: PatientCoverage;
  secondary?: PatientCoverage;
  tertiary?: PatientCoverage;
}

// ── Eligibility types ───────────────────────────────────────────────

import type { EligibilityCheckResponse } from '@store/generated/eligibility';

export type {
  EligibilityCheckResponse,
  ManualUpdateRequest,
} from '@store/generated/eligibility';

/** Feature-friendly alias for the API response */
export type { EligibilityCheckResponse as EligibilityRecord } from '@store/generated/eligibility';

export type StatusLight = 'GREEN' | 'YELLOW' | 'RED';
export type NextAction = 'none' | 'portal_review' | 'phone_verify' | 'block';
export type NextActionOwner = 'customer' | 'jotbill' | 'provider';

/**
 * Maps API status values (eligible/needs_review/blocked) to UI StatusLight values.
 */
export const API_STATUS_TO_LIGHT: Record<string, StatusLight> = {
  eligible: 'GREEN',
  needs_review: 'YELLOW',
  blocked: 'RED',
};

/**
 * Maps UI StatusLight values back to API status values for overrides.
 */
export const LIGHT_TO_API_STATUS: Record<StatusLight, string> = {
  GREEN: 'eligible',
  YELLOW: 'needs_review',
  RED: 'blocked',
};

/**
 * UI-facing eligibility snapshot — a subset of EligibilityCheckResponse
 * used by the form and status components.
 */
export interface EligibilitySnapshot {
  status_light: StatusLight;
  summary: string;
  alerts: string[];
  next_action: NextAction;
  next_action_owner: NextActionOwner;
  last_checked_at: string;
  expires_at: string;
}

/**
 * Convert an API EligibilityCheckResponse to a UI EligibilitySnapshot.
 */
/**
 * Parsed financial fields from the nested `financials` object.
 * The API returns financials as a nested object with `_cents` suffixed keys.
 * This interface provides a flat, typed view for UI components.
 */
export interface EligibilityFinancials {
  copayment: number | null;
  coinsurance: number | null;
  deductible: number | null;
  remaining_deductible: number | null;
  oop_max: number | null;
  remaining_oop_max: number | null;
  member_obligation: number | null;
  benefit_structure: string | null;
}

/**
 * Extract typed financial fields from the raw API response.
 * Converts `_cents` values to dollars for display.
 */
export function getFinancials(data: EligibilityCheckResponse | undefined): EligibilityFinancials {
  const f = data?.financials;
  if (!f) {
    return {
      copayment: null,
      coinsurance: null,
      deductible: null,
      remaining_deductible: null,
      oop_max: null,
      remaining_oop_max: null,
      member_obligation: null,
      benefit_structure: null,
    };
  }

  const cents = (key: string): number | null => {
    const v = f[key];
    return typeof v === 'number' ? v / 100 : null;
  };

  return {
    copayment: cents('copayment_cents'),
    coinsurance: typeof f.coinsurance_pct === 'number' ? f.coinsurance_pct : null,
    deductible: cents('deductible_cents'),
    remaining_deductible: cents('remaining_deductible_cents'),
    oop_max: cents('oop_max_cents'),
    remaining_oop_max: cents('remaining_oop_max_cents'),
    member_obligation: cents('member_obligation_cents'),
    benefit_structure: typeof f.benefit_structure === 'string' ? f.benefit_structure : null,
  };
}

/**
 * Parse benefit_structure into a patient-specific plain-English explanation.
 * Nirvana returns free-text like "Copay, no deductible, with OOP Max".
 * We pattern-match keywords to build an explanation of what it means for billing.
 */
export function getBenefitExplainer(fin: EligibilityFinancials): string | null {
  const bs = fin.benefit_structure?.toLowerCase();
  if (!bs) return null;

  const hasCopay = bs.includes('copay');
  const hasCoinsurance = bs.includes('coinsurance');
  const noDeductible = bs.includes('no deductible');
  const hasDeductible = !noDeductible && bs.includes('deductible');
  const hasOopMax = bs.includes('oop max') || bs.includes('out-of-pocket');

  const parts: string[] = [];

  if (hasCopay && !hasCoinsurance) {
    parts.push('Patient pays a flat copay per visit.');
  } else if (hasCoinsurance && !hasCopay) {
    parts.push('Patient pays a percentage of the visit cost (coinsurance).');
  } else if (hasCopay && hasCoinsurance) {
    parts.push('Patient pays a copay plus a percentage of remaining costs.');
  }

  if (noDeductible) {
    parts.push('Deductible does not apply to behavioral health services.');
  } else if (hasDeductible) {
    parts.push('Patient must meet their deductible before insurance pays.');
  }

  if (hasOopMax) {
    parts.push('Costs are capped at the annual out-of-pocket maximum.');
  }

  return parts.length > 0 ? parts.join(' ') : null;
}

export function apiToSnapshot(data: EligibilityCheckResponse): EligibilitySnapshot {
  return {
    status_light: API_STATUS_TO_LIGHT[data.status] ?? 'YELLOW',
    summary: data.summary ?? '',
    alerts: data.alerts ?? [],
    next_action: (data.next_action as NextAction) ?? 'none',
    next_action_owner: (data.next_action_owner as NextActionOwner) ?? 'customer',
    last_checked_at: data.last_checked_at ?? new Date().toISOString(),
    expires_at: data.expires_at ?? new Date().toISOString(),
  };
}

